import React, { useState, useEffect } from 'react';
import {
  Building2,
  Clock,
  Phone,
  Mail,
  Globe,
  MapPin,
  Save,
  Plus,
  Trash2,
  MessageSquare,
  HelpCircle,
  Package
} from 'lucide-react';
import { businessProfileAPI, phoneAPI } from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const BusinessProfilePage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Business Profile State
  const [profile, setProfile] = useState({
    businessName: '',
    description: '',
    industry: '',
    website: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    }
  });

  // Business Hours State
  const [hours, setHours] = useState({
    monday: { dayOfWeek: 'monday', openTime: '09:00', closeTime: '17:00', isOpen: true },
    tuesday: { dayOfWeek: 'tuesday', openTime: '09:00', closeTime: '17:00', isOpen: true },
    wednesday: { dayOfWeek: 'wednesday', openTime: '09:00', closeTime: '17:00', isOpen: true },
    thursday: { dayOfWeek: 'thursday', openTime: '09:00', closeTime: '17:00', isOpen: true },
    friday: { dayOfWeek: 'friday', openTime: '09:00', closeTime: '17:00', isOpen: true },
    saturday: { dayOfWeek: 'saturday', openTime: '10:00', closeTime: '14:00', isOpen: false },
    sunday: { dayOfWeek: 'sunday', openTime: '10:00', closeTime: '14:00', isOpen: false }
  });
  const [hoursTimezone, setHoursTimezone] = useState('America/New_York');

  // FAQ State
  const [faqs, setFaqs] = useState([
    { id: 1, question: '', answer: '' }
  ]);

  // Services State
  const [services, setServices] = useState([
    { id: 1, name: '', description: '', price: '' }
  ]);

  // Phone Numbers State
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [numberSearch, setNumberSearch] = useState({
    country: 'US',
    areaCode: '',
    contains: ''
  });

  useEffect(() => {
    const loadHours = async () => {
      try {
        const response = await businessProfileAPI.getBusinessHours();
        if (response.data?.data) {
          const hoursMap = {};
          let detectedTimezone = null;

          response.data.data.forEach(hour => {
            hoursMap[hour.dayOfWeek] = {
              dayOfWeek: hour.dayOfWeek,
              openTime: hour.openTime || '09:00',
              closeTime: hour.closeTime || '17:00',
              isOpen: hour.isOpen !== false
            };

            if (!detectedTimezone && hour.timezone) {
              detectedTimezone = hour.timezone;
            }
          });

          const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
          days.forEach(day => {
            if (!hoursMap[day]) {
              hoursMap[day] = {
                dayOfWeek: day,
                openTime: '09:00',
                closeTime: '17:00',
                isOpen: day !== 'saturday' && day !== 'sunday'
              };
            }
          });

          setHours(hoursMap);
          if (detectedTimezone) {
            setHoursTimezone(detectedTimezone);
          }
        }
      } catch (error) {
        console.error('Failed to load hours:', error);
      }
    };

    loadHours();
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [profileRes, faqRes, servicesRes, numbersRes] = await Promise.all([
        businessProfileAPI.getProfile().catch(() => ({ data: profile })),
        businessProfileAPI.getFAQ().catch(() => ({ data: faqs })),
        businessProfileAPI.getServices().catch(() => ({ data: services })),
        phoneAPI.getMyNumbers().catch(() => ({ data: [] }))
      ]);

      // Load profile data with fallback to default state
      const loadedProfile = profileRes.data?.data || profileRes.data || profile;

      // Pre-populate with tenant data if fields are empty (this shows existing business info)
      setProfile({
        businessName: loadedProfile.businessName || loadedProfile.companyName || profile.businessName,
        description: loadedProfile.description || profile.description,
        industry: loadedProfile.industry || profile.industry,
        website: loadedProfile.website || profile.website,
        email: loadedProfile.email || profile.email,
        phone: loadedProfile.phone || loadedProfile.phoneNumber || profile.phone,
        address: {
          street: loadedProfile.address || loadedProfile.street || profile.address.street,
          city: loadedProfile.city || profile.address.city,
          state: loadedProfile.state || profile.address.state,
          zipCode: loadedProfile.zipCode || loadedProfile.zip || profile.address.zipCode,
          country: loadedProfile.country || profile.address.country || 'US'
        }
      });

      setFaqs(faqRes.data?.data?.length > 0 ? faqRes.data.data : faqRes.data?.length > 0 ? faqRes.data : faqs);
      setServices(servicesRes.data?.data?.length > 0 ? servicesRes.data.data : servicesRes.data?.length > 0 ? servicesRes.data : services);
      setPhoneNumbers(numbersRes.data?.data || numbersRes.data || []);
    } catch (error) {
      console.error('Failed to load business data:', error);
      toast.error('Failed to load business information');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    try {
      setSaving(true);

      const payload = {
        businessName: profile.businessName,
        description: profile.description,
        industry: profile.industry,
        website: profile.website,
        email: profile.email,
        phoneNumber: profile.phone,
        address: profile.address.street,
        city: profile.address.city,
        state: profile.address.state,
        zipCode: profile.address.zipCode,
        country: profile.address.country
      };

      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      const response = await businessProfileAPI.updateProfile(payload);
      const savedProfile = response.data?.data || response.data?.profile;

      if (savedProfile) {
        setProfile({
          businessName: savedProfile.businessName || payload.businessName,
          description: savedProfile.description || payload.description,
          industry: savedProfile.industry || payload.industry,
          website: savedProfile.website || payload.website,
          email: savedProfile.email || payload.email,
          phone: savedProfile.phoneNumber || savedProfile.phone || payload.phoneNumber,
          address: {
            street: savedProfile.address || payload.address || '',
            city: savedProfile.city || payload.city || '',
            state: savedProfile.state || payload.state || '',
            zipCode: savedProfile.zipCode || payload.zipCode || '',
            country: savedProfile.country || payload.country || 'US'
          }
        });
      }

      toast.success('Business profile updated successfully');
    } catch (error) {
      console.error('Failed to update business profile:', error);
      toast.error('Failed to update business profile');
    } finally {
      setSaving(false);
    }
  };

  const saveBusinessHours = async () => {
    try {
      setSaving(true);

      // Transform frontend format to backend format
      const backendHours = { timezone: hoursTimezone };
      Object.entries(hours).forEach(([day, dayHours]) => {
        backendHours[day] = {
          isOpen: dayHours.isOpen !== false,
          openTime: dayHours.openTime || '09:00',
          closeTime: dayHours.closeTime || '17:00',
          timezone: hoursTimezone
        };
      });

      const response = await businessProfileAPI.updateBusinessHours(backendHours);
      const savedHours = response.data?.data;

      if (Array.isArray(savedHours)) {
        const updatedMap = {};
        let detectedTimezone = hoursTimezone;

        savedHours.forEach((hour) => {
          updatedMap[hour.dayOfWeek] = {
            dayOfWeek: hour.dayOfWeek,
            openTime: hour.openTime || '09:00',
            closeTime: hour.closeTime || '17:00',
            isOpen: hour.isOpen !== false
          };

          if (hour.timezone) {
            detectedTimezone = hour.timezone;
          }
        });

        const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        allDays.forEach((day) => {
          if (!updatedMap[day]) {
            updatedMap[day] = {
              dayOfWeek: day,
              openTime: '09:00',
              closeTime: '17:00',
              isOpen: day !== 'saturday' && day !== 'sunday'
            };
          }
        });

        setHours(updatedMap);
        setHoursTimezone(detectedTimezone);
      }
      toast.success('Business hours updated successfully');
    } catch (error) {
      console.error('Failed to save business hours:', error);
      toast.error('Failed to update business hours');
    } finally {
      setSaving(false);
    }
  };

  const saveFAQs = async () => {
    try {
      setSaving(true);
      await businessProfileAPI.updateFAQ(faqs.filter(f => f.question && f.answer));
      toast.success('FAQs updated successfully');
    } catch (error) {
      toast.error('Failed to update FAQs');
    } finally {
      setSaving(false);
    }
  };

  const saveServices = async () => {
    try {
      setSaving(true);
      await businessProfileAPI.updateServices(services.filter(s => s.name));
      toast.success('Services updated successfully');
    } catch (error) {
      toast.error('Failed to update services');
    } finally {
      setSaving(false);
    }
  };

  const searchPhoneNumbers = async () => {
    try {
      setSearchingNumbers(true);
      const response = await phoneAPI.getAvailableNumbers(numberSearch);
      setAvailableNumbers(response.data);
      if (response.data.length === 0) {
        toast.error('No available numbers found for your criteria');
      }
    } catch (error) {
      toast.error('Failed to search for phone numbers');
    } finally {
      setSearchingNumbers(false);
    }
  };

  const provisionPhoneNumber = async (number) => {
    try {
      await phoneAPI.provisionNumber(number);
      toast.success(`Successfully provisioned ${number}`);
      setPhoneNumbers([...phoneNumbers, { number, status: 'active' }]);
      setAvailableNumbers(availableNumbers.filter(n => n.number !== number));
    } catch (error) {
      toast.error('Failed to provision phone number');
    }
  };

  const addFAQ = () => {
    setFaqs([...faqs, { id: Date.now(), question: '', answer: '' }]);
  };

  const removeFAQ = (id) => {
    setFaqs(faqs.filter(f => f.id !== id));
  };

  const updateFAQ = (id, field, value) => {
    setFaqs(faqs.map(f => f.id === id ? { ...f, [field]: value } : f));
  };

  const addService = () => {
    setServices([...services, { id: Date.now(), name: '', description: '', price: '' }]);
  };

  const removeService = (id) => {
    setServices(services.filter(s => s.id !== id));
  };

  const updateService = (id, field, value) => {
    setServices(services.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading business profile..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Business Profile</h1>
        <p className="text-gray-600 mt-1">
          Configure your business information and AI assistant settings
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'general', label: 'General Info', icon: Building2 },
            { id: 'hours', label: 'Business Hours', icon: Clock },
            { id: 'faq', label: 'FAQ', icon: HelpCircle },
            { id: 'services', label: 'Services', icon: Package }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                ${activeTab === tab.id
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* General Info Tab */}
      {activeTab === 'general' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">General Business Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Name
              </label>
              <input
                type="text"
                value={profile.businessName}
                onChange={(e) => setProfile({ ...profile, businessName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your Business Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Industry
              </label>
              <select
                value={profile.industry}
                onChange={(e) => setProfile({ ...profile, industry: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Industry</option>
                <option value="retail">Retail</option>
                <option value="healthcare">Healthcare</option>
                <option value="finance">Finance</option>
                <option value="technology">Technology</option>
                <option value="education">Education</option>
                <option value="hospitality">Hospitality</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Description
              </label>
              <textarea
                value={profile.description}
                onChange={(e) => setProfile({ ...profile, description: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe your business, products, and services..."
              />
              <p className="text-sm text-gray-500 mt-1">
                This helps the AI assistant understand your business better
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Globe className="inline w-4 h-4 mr-1" /> Website
              </label>
              <input
                type="url"
                value={profile.website}
                onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://www.yourbusiness.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="inline w-4 h-4 mr-1" /> Email
              </label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="contact@yourbusiness.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="inline w-4 h-4 mr-1" /> Main Phone
              </label>
              <input
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="inline w-4 h-4 mr-1" /> Street Address
              </label>
              <input
                type="text"
                value={profile.address.street}
                onChange={(e) => setProfile({
                  ...profile,
                  address: { ...profile.address, street: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="123 Main St"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={saveProfile}
              disabled={saving}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save Profile'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Business Hours Tab */}
      {activeTab === 'hours' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Business Hours</h2>

          <div className="space-y-4">
            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
              <div key={day} className="flex items-center space-x-4">
                <label className="w-24 capitalize">{day}</label>
                <input
                  type="checkbox"
                  checked={hours[day]?.isOpen}
                  onChange={(e) => setHours({
                    ...hours,
                    [day]: { ...hours[day], isOpen: e.target.checked }
                  })}
                />
                <input
                  type="time"
                  value={hours[day]?.openTime || '09:00'}
                  onChange={(e) => setHours({
                    ...hours,
                    [day]: { ...hours[day], openTime: e.target.value }
                  })}
                  disabled={!hours[day]?.isOpen}
                  className="px-3 py-2 border rounded"
                />
                <span>to</span>
                <input
                  type="time"
                  value={hours[day]?.closeTime || '17:00'}
                  onChange={(e) => setHours({
                    ...hours,
                    [day]: { ...hours[day], closeTime: e.target.value }
                  })}
                  disabled={!hours[day]?.isOpen}
                  className="px-3 py-2 border rounded"
                />
              </div>
            ))}

            <div className="pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timezone
              </label>
              <select
                value={hoursTimezone}
                onChange={(e) => setHoursTimezone(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </select>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={saveBusinessHours}
              disabled={saving}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save Hours'}</span>
            </button>
          </div>
        </div>
      )}

      {/* FAQ Tab */}
      {activeTab === 'faq' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Frequently Asked Questions</h2>
          <p className="text-sm text-gray-600 mb-4">
            These FAQs help the AI assistant answer common customer questions
          </p>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={faq.id} className="border rounded-lg p-4">
                <div className="flex items-start space-x-4">
                  <span className="text-gray-500 font-medium">Q{index + 1}:</span>
                  <div className="flex-1 space-y-3">
                    <input
                      type="text"
                      placeholder="Question"
                      value={faq.question}
                      onChange={(e) => updateFAQ(faq.id, 'question', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <textarea
                      placeholder="Answer"
                      value={faq.answer}
                      onChange={(e) => updateFAQ(faq.id, 'answer', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <button
                    onClick={() => removeFAQ(faq.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <button
              onClick={addFAQ}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <Plus className="w-4 h-4" />
              <span>Add FAQ</span>
            </button>

            <button
              onClick={saveFAQs}
              disabled={saving}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save FAQs'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Products & Services</h2>
          <p className="text-sm text-gray-600 mb-4">
            Define your offerings so the AI can accurately describe them to customers
          </p>

          <div className="space-y-4">
            {services.map((service, index) => (
              <div key={service.id} className="border rounded-lg p-4">
                <div className="flex items-start space-x-4">
                  <span className="text-gray-500 font-medium">#{index + 1}</span>
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      type="text"
                      placeholder="Service name"
                      value={service.name}
                      onChange={(e) => updateService(service.id, 'name', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <input
                      type="text"
                      placeholder="Price (optional)"
                      value={service.price}
                      onChange={(e) => updateService(service.id, 'price', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <div className="md:col-span-3">
                      <textarea
                        placeholder="Description"
                        value={service.description}
                        onChange={(e) => updateService(service.id, 'description', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => removeService(service.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <button
              onClick={addService}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <Plus className="w-4 h-4" />
              <span>Add Service</span>
            </button>

            <button
              onClick={saveServices}
              disabled={saving}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save Services'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessProfilePage;
